import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Camera,
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Send,
  Star,
  CalendarDays,
  X,
  MessageSquareText,
} from "lucide-react";

/**
 * Photo Pipeline — the GM's 20-shot merchandising standard.
 * The Photo Manager owns every shot. Bottleneck alerts fire when a
 * car sits >24h waiting.
 */

const BRAND_BRONZE = "#a06a1e";
const BRAND_BRONZE_DARK = "#7a4e0f";
const BRAND_CREAM = "#f6ecd8";

const STAGES = [
  { key: "not_started", label: "Not Started",  Icon: Clock,          tint: "bg-neutral-800 text-white" },
  { key: "in_progress", label: "In Progress",  Icon: Camera,         tint: "bg-blue-600 text-white" },
  { key: "done",        label: "Done · Ready to List", Icon: CheckCircle2, tint: "bg-emerald-600 text-white" },
];

function ageDot(hours) {
  if (hours == null) return "bg-neutral-400";
  if (hours <= 12) return "bg-emerald-500";
  if (hours <= 24) return "bg-yellow-500";
  return "bg-red-500";
}

export default function PhotoPipelinePage() {
  const [data, setData] = useState(null);
  const [digest, setDigest] = useState(null);
  const [alerting, setAlerting] = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [activeSlot, setActiveSlot] = useState(null);
  const [bookForm, setBookForm] = useState({
    vehicle_id: "",
    car_owner_name: "",
    car_owner_phone: "",
    notes: "",
  });
  const [booking, setBooking] = useState(false);

  const load = async () => {
    try {
      const [p, d, s] = await Promise.all([
        api.get("/posse/photos").then((r) => r.data),
        api.get("/posse/photos/digest").then((r) => r.data),
        api.get("/posse/photos/slots").then((r) => r.data),
      ]);
      setData(p);
      setDigest(d);
      setSchedule(s);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const alertTanya = async () => {
    setAlerting(true);
    try {
      const { data } = await api.post("/posse/photos/alert-tanya");
      if (data.sent_count === 0) {
        toast.success("Nothing to alert — pipeline is clean.");
      } else {
        toast.success(`Alert sent to Photo Manager + GM (${data.sent_count} texts)`);
      }
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setAlerting(false);
    }
  };

  // Awaiting-shoot vehicles = candidates when booking a slot
  const bookableVehicles = useMemo(() => {
    const from = (data?.stages?.not_started || []).concat(data?.stages?.in_progress || []);
    return from.map((v) => ({
      id: v.id,
      stock_no: v.stock_no,
      label: `${v.year} ${v.make_model}`,
      vin: v.vin_last6,
      hours_waiting: v.photo_hours_waiting,
    }));
  }, [data]);

  const openBookDialog = (slot) => {
    setActiveSlot(slot);
    setBookForm({ vehicle_id: "", car_owner_name: "", car_owner_phone: "", notes: "" });
  };

  const submitBooking = async () => {
    if (!bookForm.vehicle_id) {
      toast.error("Pick a car first");
      return;
    }
    const veh = bookableVehicles.find((v) => v.id === bookForm.vehicle_id);
    setBooking(true);
    try {
      await api.post(`/posse/photos/slots/${activeSlot.id}/book`, {
        vehicle_id: bookForm.vehicle_id,
        vehicle_label: veh?.label,
        vehicle_stock_no: veh?.stock_no,
        car_owner_name: bookForm.car_owner_name,
        car_owner_phone: bookForm.car_owner_phone,
        notes: bookForm.notes,
      });
      toast.success(
        bookForm.car_owner_phone
          ? "Slot booked · confirmation text sent"
          : "Slot booked"
      );
      setActiveSlot(null);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setBooking(false);
    }
  };

  const cancelSlot = async (slot) => {
    try {
      await api.post(`/posse/photos/slots/${slot.id}/cancel`);
      toast.success(`Slot ${slot.time} freed up`);
      await load();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  const totals = data?.totals || {};
  const bottleneck = data?.bottleneck || [];
  const shots = data?.shots_standard || [];

  return (
    <DashboardShell>
      {/* Hero */}
      <div
        className="mb-4 rounded-xl overflow-hidden shadow-xl relative"
        style={{
          background: `linear-gradient(135deg, ${BRAND_BRONZE_DARK} 0%, ${BRAND_BRONZE} 60%, #c98c3d 100%)`,
        }}
        data-testid="photo-pipeline-hero"
      >
        <div className="relative p-5 md:p-6 flex items-center gap-4 flex-wrap">
          <div
            className="w-14 h-14 rounded-full grid place-items-center shadow-xl shrink-0"
            style={{ background: BRAND_CREAM }}
          >
            <Camera className="w-7 h-7" strokeWidth={2.5} style={{ color: BRAND_BRONZE_DARK }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-label text-[0.6rem] text-white/85 tracking-widest">
              PREOWNED PIPELINE · PHOTO PIPELINE
            </div>
            <div className="font-display font-black text-3xl md:text-4xl tracking-tighter text-white leading-none mt-1">
              Photo Pipeline
            </div>
            <div className="font-display text-white/90 text-sm md:text-base mt-1">
              Photo Manager · 20-shot standard · nothing goes online till the list is done
            </div>
          </div>
          <Link to="/posse">
            <Button
              variant="outline"
              className="bg-white/95 hover:bg-white text-neutral-900 border-transparent"
              data-testid="photo-back"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
              Back to Pipeline
            </Button>
          </Link>
        </div>
      </div>

      {/* KPI strip + bottleneck banner */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="p-4 border-border" data-testid="photo-kpi-awaiting">
          <div className="font-mono-label text-muted-foreground text-[0.6rem]">Awaiting Shoot</div>
          <div className="font-display font-black text-3xl tabular-nums mt-1">
            {totals.not_started ?? 0}
          </div>
          <div className="text-[0.6rem] text-muted-foreground">Zero shots taken</div>
        </Card>
        <Card className="p-4 border-border" data-testid="photo-kpi-in-progress">
          <div className="font-mono-label text-blue-800 text-[0.6rem]">In Progress</div>
          <div className="font-display font-black text-3xl tabular-nums mt-1 text-blue-800">
            {totals.in_progress ?? 0}
          </div>
          <div className="text-[0.6rem] text-blue-700/70">Some shots captured</div>
        </Card>
        <Card className="p-4 border-border !bg-emerald-50 dark:!bg-emerald-950/20" data-testid="photo-kpi-done">
          <div className="font-mono-label text-emerald-800 text-[0.6rem]">Ready to List</div>
          <div className="font-display font-black text-3xl tabular-nums mt-1 text-emerald-800">
            {totals.done ?? 0}
          </div>
          <div className="text-[0.6rem] text-emerald-700/70">All 20 shots in</div>
        </Card>
        <Card
          className={`p-4 border-border ${
            totals.bottleneck > 0
              ? "!bg-red-50 dark:!bg-red-950/20 border-red-400"
              : "!bg-emerald-50/40"
          }`}
          data-testid="photo-kpi-bottleneck"
        >
          <div
            className={`font-mono-label text-[0.6rem] flex items-center gap-1 ${
              totals.bottleneck > 0 ? "text-red-700" : "text-emerald-800"
            }`}
          >
            <AlertTriangle className="w-3 h-3" strokeWidth={2} />
            Waiting &gt; 24hr
          </div>
          <div
            className={`font-display font-black text-3xl tabular-nums mt-1 ${
              totals.bottleneck > 0 ? "text-red-700" : "text-emerald-800"
            }`}
          >
            {totals.bottleneck ?? 0}
          </div>
          <div className="text-[0.6rem] text-muted-foreground">
            Oldest: {totals.oldest_waiting_hours ?? 0}h
          </div>
        </Card>
      </div>

      {/* Alert banner + digest preview */}
      {bottleneck.length > 0 && (
        <Card
          className="p-4 mb-4 border-2 border-red-400 bg-red-50/70 dark:bg-red-950/20"
          data-testid="photo-alert-banner"
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <AlertTriangle className="w-4 h-4 text-red-700" strokeWidth={2.4} />
            <div className="font-mono-label text-[0.65rem] tracking-widest text-red-800">
              {bottleneck.length} CAR{bottleneck.length === 1 ? "" : "S"} PAST 24HR ON PHOTOS
            </div>
            <Button
              size="sm"
              onClick={alertTanya}
              disabled={alerting}
              className="ml-auto bg-red-600 hover:bg-red-500 text-white h-8"
              data-testid="photo-alert-send"
            >
              <Send className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.4} />
              {alerting ? "Sending…" : "Text Photo Manager + GM Now"}
            </Button>
          </div>
          <p className="text-xs text-neutral-700 mb-2">
            Bottleneck alert fires an auto-text to the Photo Manager and the GM naming every car
            past the 24-hour photo budget. Silence is not an option.
          </p>
          {digest?.message && (
            <div
              className="rounded-md p-3 border font-mono text-[0.75rem] leading-relaxed"
              style={{ background: BRAND_CREAM, borderColor: BRAND_BRONZE, color: "#1a1a1a" }}
              data-testid="photo-alert-preview"
            >
              {digest.message}
            </div>
          )}
        </Card>
      )}

      {/* 3-lane kanban */}
      <div
        className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4"
        data-testid="photo-kanban"
      >
        {STAGES.map((col) => {
          const list = data?.stages?.[col.key] || [];
          return (
            <div
              key={col.key}
              className="min-w-0"
              data-testid={`photo-col-${col.key}`}
            >
              <div className={`px-3 py-2 rounded-t-md flex items-center gap-2 ${col.tint}`}>
                <col.Icon className="w-4 h-4" strokeWidth={2.2} />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm truncate">{col.label}</div>
                </div>
                <div className="font-display font-black text-lg tabular-nums">
                  {list.length}
                </div>
              </div>
              <div className="border border-t-0 border-border rounded-b-md p-2 bg-muted/20 space-y-2 min-h-[240px]">
                {list.length === 0 && (
                  <div className="text-xs text-muted-foreground text-center py-8">
                    Empty stage.
                  </div>
                )}
                {list.map((v) => {
                  const pct = Math.round(
                    ((v.photo_shots_complete || 0) / (v.photo_shots_total || 20)) * 100
                  );
                  const isBottleneck = v.photo_bottleneck;
                  return (
                    <div
                      key={v.id}
                      className={`rounded-md p-3 border border-border bg-background shadow-sm ${
                        isBottleneck ? "border-l-4 border-l-red-500 bg-red-50/40" : ""
                      }`}
                      data-testid={`photo-card-${v.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-mono text-[0.6rem] text-muted-foreground">
                            {v.stock_no}
                          </div>
                          <div className="font-display font-bold text-sm truncate">
                            {v.year} {v.make_model}
                          </div>
                          <div className="text-[0.6rem] text-muted-foreground font-mono">
                            VIN …{v.vin_last6}
                          </div>
                        </div>
                        <div className="flex flex-col items-end shrink-0">
                          <span
                            className={`w-2 h-2 rounded-full ${ageDot(v.photo_hours_waiting)}`}
                            aria-hidden
                          />
                          <div className="font-mono-label text-[0.5rem] text-muted-foreground mt-0.5 tabular-nums">
                            {v.photo_hours_waiting}h
                          </div>
                        </div>
                      </div>
                      {/* Progress bar */}
                      <div className="mt-2">
                        <div className="flex justify-between text-[0.55rem] text-muted-foreground mb-0.5">
                          <span className="font-mono-label tracking-widest">
                            {v.photo_shots_complete}/{v.photo_shots_total} SHOTS
                          </span>
                          <span className="tabular-nums">{pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-neutral-200 overflow-hidden">
                          <div
                            className={`h-full ${
                              pct === 100
                                ? "bg-emerald-500"
                                : pct >= 50
                                ? "bg-blue-500"
                                : "bg-neutral-500"
                            }`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      {isBottleneck && (
                        <div className="mt-2 text-[0.6rem] text-red-700 font-bold flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" strokeWidth={2.4} />
                          Bottleneck · {v.photo_hours_waiting}h past 24hr
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* The 20-shot checklist reference */}
      <Card
        className="p-4 border-2"
        style={{ borderColor: BRAND_BRONZE, background: `${BRAND_CREAM}55` }}
        data-testid="photo-checklist-standard"
      >
        <div className="flex items-center gap-2 mb-2">
          <Star className="w-4 h-4" style={{ color: BRAND_BRONZE_DARK }} strokeWidth={2.4} />
          <div className="font-mono-label text-[0.6rem] tracking-widest" style={{ color: BRAND_BRONZE_DARK }}>
            THE GM'S 20-SHOT STANDARD · TANYA'S BIBLE
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[0.72rem]">
          {shots.map((s, idx) => (
            <div key={idx} className="flex items-center gap-1.5">
              <span
                className="w-4 h-4 rounded-full grid place-items-center text-[0.55rem] font-bold"
                style={{ background: BRAND_BRONZE_DARK, color: "white" }}
              >
                {idx + 1}
              </span>
              <span className="text-neutral-800 truncate" title={s}>
                {s}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* PHOTOG SCHEDULER — 5-day rolling calendar */}
      {schedule && (
        <Card
          className="p-5 mt-4 border-2"
          style={{ borderColor: BRAND_BRONZE_DARK, background: "#0f172a" }}
          data-testid="photo-scheduler"
        >
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <CalendarDays className="w-4 h-4 text-yellow-300" strokeWidth={2.4} />
            <div className="font-mono-label text-[0.65rem] tracking-widest text-yellow-300">
              PHOTOG SCHEDULER · TANYA <span className="italic">"DOLLY PARTON"</span> · 5-DAY VIEW
            </div>
            <div className="ml-auto text-[0.6rem] text-white/70 italic">
              Book a slot → auto-text confirms the shoot to the car owner
            </div>
          </div>
          <div className="grid md:grid-cols-5 gap-3">
            {schedule.days.map((day) => {
              const d = new Date(day.date + "T00:00:00");
              const label = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
              return (
                <div
                  key={day.date}
                  className="rounded-md border border-white/10 bg-black/30 p-2"
                  data-testid={`photo-scheduler-day-${day.date}`}
                >
                  <div className="flex items-baseline justify-between mb-2">
                    <div className="font-display font-bold text-sm text-yellow-100">{label}</div>
                    <div className="text-[0.55rem] font-mono text-yellow-300">
                      {day.booked_count}/{day.slots.length}
                    </div>
                  </div>
                  <div className="space-y-1">
                    {day.slots.map((slot) => {
                      const isBooked = slot.status === "booked";
                      return (
                        <div
                          key={slot.id}
                          className={`rounded-sm px-2 py-1.5 text-[0.65rem] flex items-center justify-between gap-1 ${
                            isBooked
                              ? "bg-emerald-800/50 border border-emerald-500/40 text-emerald-100"
                              : "bg-yellow-500/10 border border-yellow-500/30 text-yellow-100 hover:bg-yellow-500/25 cursor-pointer"
                          }`}
                          data-testid={`photo-slot-${slot.id}`}
                        >
                          <span className="font-mono tabular-nums">{slot.time}</span>
                          {isBooked ? (
                            <>
                              <span className="truncate flex-1 mx-1" title={slot.vehicle_label}>
                                {slot.vehicle_stock_no || slot.vehicle_label}
                              </span>
                              <button
                                onClick={() => cancelSlot(slot)}
                                className="text-red-300 hover:text-red-100"
                                title="Cancel booking"
                                data-testid={`photo-slot-cancel-${slot.id}`}
                              >
                                <X className="w-3 h-3" strokeWidth={2.4} />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => openBookDialog(slot)}
                              className="ml-auto text-yellow-200 hover:text-yellow-100 font-semibold"
                              data-testid={`photo-slot-book-${slot.id}`}
                            >
                              Book +
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Book slot dialog */}
      <Dialog open={!!activeSlot} onOpenChange={(open) => !open && setActiveSlot(null)}>
        <DialogContent className="max-w-md" data-testid="photo-book-dialog">
          <DialogHeader>
            <DialogTitle>
              Book photo slot · {activeSlot?.date} at {activeSlot?.time}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="font-mono-label text-[0.55rem] tracking-widest text-neutral-700 mb-1 block">
                CAR · {bookableVehicles.length} awaiting shoot
              </label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={bookForm.vehicle_id}
                onChange={(e) => setBookForm({ ...bookForm, vehicle_id: e.target.value })}
                data-testid="photo-book-vehicle"
              >
                <option value="">— pick a car —</option>
                {bookableVehicles.map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.stock_no} · {v.label} · {v.hours_waiting}h waiting
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-mono-label text-[0.55rem] tracking-widest text-neutral-700 mb-1 block">
                CAR OWNER NAME (optional)
              </label>
              <Input
                placeholder="e.g. Sales manager or trade-in customer"
                value={bookForm.car_owner_name}
                onChange={(e) => setBookForm({ ...bookForm, car_owner_name: e.target.value })}
                data-testid="photo-book-owner-name"
              />
            </div>
            <div>
              <label className="font-mono-label text-[0.55rem] tracking-widest text-neutral-700 mb-1 block">
                OWNER PHONE (auto-text confirms shoot)
              </label>
              <Input
                placeholder="e.g. 913-555-0123"
                value={bookForm.car_owner_phone}
                onChange={(e) => setBookForm({ ...bookForm, car_owner_phone: e.target.value })}
                data-testid="photo-book-owner-phone"
              />
              <div className="text-[0.6rem] text-neutral-500 mt-1 italic flex items-center gap-1">
                <MessageSquareText className="w-3 h-3" />
                Leave blank to book without a text.
              </div>
            </div>
            <div>
              <label className="font-mono-label text-[0.55rem] tracking-widest text-neutral-700 mb-1 block">
                NOTES (optional)
              </label>
              <Input
                placeholder="e.g. Bring lens for tight garage bay"
                value={bookForm.notes}
                onChange={(e) => setBookForm({ ...bookForm, notes: e.target.value })}
                data-testid="photo-book-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActiveSlot(null)}>
              Cancel
            </Button>
            <Button
              onClick={submitBooking}
              disabled={booking}
              className="font-bold"
              style={{ background: BRAND_BRONZE_DARK, color: "white" }}
              data-testid="photo-book-submit"
            >
              {booking ? "Booking…" : "Book & Text Confirmation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
